import {
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from "@fluentui/react-components";
import {
  getAllWorkspaces,
  getAnnouncements,
  getCategories,
  getUseCasePrompts,
  getUseCases,
  getUsers,
} from "api";
import { Constants } from "common/constants";
import { ManageAccessLLMModels } from "components/manageAccessLLMModels/ManageAccessLLMModels";
import { ManageAnnouncements } from "components/manageAnnouncements/ManageAnnouncements";
import { ManageCategories } from "components/manageCategories/ManageCategories";
import { ManageFeedback } from "components/manageFeedback/ManageFeedback";
import { ManageInterviewsStatistics } from "components/manageInterviewsStatistics/ManageInterviewsStatistics";
import { ManageStatistics } from "components/manageStatistics/ManageStatistics";
import { ManageTags } from "components/manageTags/ManageTags";
import { ManageUseCasePromptsListView } from "components/manageUseCasePrompts/ManageUseCasePromptsListView";
import { ManageUseCases } from "components/manageUseCases/ManageUseCases";
import { ManageUsers } from "components/manageUsers/ManageUsers";
import { ManageTemplates } from "components/manageTemplates/ManageTemplates";
import { ManageWorkspacesListView } from "components/manageWorkspaces/ManageWorkspacesListView";
import { ManageHelpPageDocuments } from "components/manageHelpPageDocuments/ManageHelpPageDocuments";
import { NavigationPanel } from "components/navigationPanel/NavigationPanel";
import { AdminSidebar } from "components/adminSidebar";
import {
  findItemBySlug,
  getSlugForKey,
  getVisibleItems,
} from "components/adminSidebar/adminSidebarConfig";
import type { AdminSidebarRole } from "components/adminSidebar/adminSidebar.types";
import { IAnnouncement } from "models/IAnnouncement";
import { IUseCase } from "models/IUseCase";
import { IWorkspace } from "models/IWorkspace";
import { useCallback, useEffect } from "react";
import { useIsMobile } from "hooks/useIsMobile";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { setAnnouncements } from "../redux/announcementsSlice";
import {
  setUseCasePrompts,
  setUseCasePromptsLoading,
} from "../redux/useCasePromptsSlice";
import { setUseCases, setUseCasesLoading } from "../redux/useCasesSlice";
import { setUsers } from "../redux/users";
import { setWorkspaces } from "../redux/workspacesSlice";
import { RootState } from "../store";
import { designTokens } from "theme/customTheme";

const useAdminPageStyles = makeStyles({
  container: {
    display: "flex",
    height: "100dvh",
    ...shorthands.overflow("hidden"),
  },
  navPanel: {
    display: "flex",
    flexWrap: "wrap",
    height: "100dvh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    height: "100dvh",
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.overflow("hidden"),
  },
  contentContainerMobile: {
    paddingTop: designTokens.spacing[16],
  },
  contentBody: {
    flexGrow: 1,
    ...shorthands.overflow("auto"),
  },
});

export const AdminPage = (): JSX.Element => {
  const classes = useAdminPageStyles();
  const isMobile = useIsMobile();
  const { section: slugParam } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  ) as { roles: string[] } | null;

  const isAdmin = user?.roles.includes("admin");
  const isPlatformAdmin = user?.roles.includes("platformAdmin");

  const userRole: AdminSidebarRole = isPlatformAdmin
    ? "platformAdmin"
    : isAdmin
      ? "admin"
      : "";

  const selectedSection =
    (slugParam && findItemBySlug(slugParam, userRole)?.key) ?? "tags";

  useEffect(() => {
    if (!slugParam) {
      const visible = getVisibleItems(userRole);
      const firstKey = visible[0]?.items[0]?.key ?? "tags";
      const slug = getSlugForKey(firstKey) ?? "knowledge-components";
      navigate(`/admin/${slug}`, { replace: true });
    } else if (!findItemBySlug(slugParam, userRole)) {
      navigate("/admin/knowledge-components", { replace: true });
    }
  }, [slugParam, navigate, userRole]);

  const handleSelect = useCallback(
    (key: string) => {
      const slug = getSlugForKey(key);
      if (slug) {
        navigate(`/admin/${slug}`);
      }
    },
    [navigate],
  );

  const {
    data: categories,
    isLoading: areCategoriesLoading,
    refetch: refetchCategories,
    isRefetching: categoriesAreRefetching,
  } = useQuery(
    "categories",
    async () => {
      const categories = await getCategories(true);
      return categories;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
    },
  );

  useEffect((): void => {
    const fetchUsers = async () => {
      if (isPlatformAdmin) {
        const elevatedRoles: string[] = [
          Constants.ADMINS_GROUP_NAME_KEY,
          Constants.PLATFORM_ADMINS_GROUP_NAME_KEY,
          Constants.DECOODA_ADMINS_GROUP_NAME_KEY,
          Constants.DIAGNOSTICS_ADMINS_GROUP_NAME_KEY,
          Constants.REPORTING_GROUP_NAME_KEY,
          Constants.AI_METRICS_USERS_GROUP_NAME_KEY,
          Constants.AI_METRICS_ADMINS_GROUP_NAME_KEY,
          Constants.MCP_USER_GROUP_NAME_KEY,
          Constants.PROJECT_IQ_ADMINS_GROUP_NAME_KEY,
        ] as string[];

        const users = await getUsers({
          text: undefined,
          hasOnlyOurUsers: false,
          requiredRoles: elevatedRoles as never[],
        });

        dispatch(setUsers(users));
      }
    };
    fetchUsers();
  }, [dispatch, isPlatformAdmin]);

  useEffect((): void => {
    const fetchAnnouncements = async () => {
      try {
        const data: IAnnouncement[] = await getAnnouncements();
        dispatch(setAnnouncements(data));
      } catch (e) {
        console.error("Error fetching announcements:", e);
        dispatch(setAnnouncements([]));
      }
    };
    fetchAnnouncements();
  }, [dispatch]);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      const data: IWorkspace[] = await getAllWorkspaces();
      dispatch(setWorkspaces(data));
    };

    if (isPlatformAdmin) {
      fetchWorkspaces();
    }

    return () => {
      dispatch(setWorkspaces([]));
    };
  }, [dispatch, isPlatformAdmin]);

  useEffect((): void => {
    const fetchUseCases = async () => {
      try {
        dispatch(setUseCasesLoading(true));
        const data: IUseCase[] = await getUseCases();
        dispatch(setUseCases(data));
      } catch (e) {
        console.error("Error loading data:", e);
        dispatch(setUseCasesLoading(false));
      }
    };

    fetchUseCases();
  }, [dispatch]);

  useEffect((): void => {
    const fetchUseCasePrompts = async () => {
      try {
        dispatch(setUseCasePromptsLoading(true));
        const data = await getUseCasePrompts();
        dispatch(setUseCasePrompts(data));
      } catch (e) {
        console.error("Error loading use case prompts:", e);
        dispatch(setUseCasePromptsLoading(false));
      }
    };

    fetchUseCasePrompts();
  }, [dispatch]);

  useEffect((): void => {
    if (selectedSection === "tags" || selectedSection === "categories") {
      refetchCategories();
    }
  }, [selectedSection, refetchCategories]);

  const renderContent = (): JSX.Element | null => {
    switch (selectedSection) {
      case "tags":
      case "categories":
        return selectedSection === "tags" ? (
          <ManageTags
            key={selectedSection}
            categories={categories ?? []}
            isLoading={areCategoriesLoading || categoriesAreRefetching}
          />
        ) : (
          <ManageCategories
            key={selectedSection}
            categories={categories ?? []}
            isLoading={areCategoriesLoading || categoriesAreRefetching}
          />
        );
      case "users":
        return isPlatformAdmin ? <ManageUsers /> : null;
      case "statistics":
        return isPlatformAdmin || isAdmin ? <ManageStatistics /> : null;
      case "interviewsStatistics":
        return isPlatformAdmin || isAdmin ? (
          <ManageInterviewsStatistics />
        ) : null;
      case "announcements":
        return isPlatformAdmin ? <ManageAnnouncements /> : null;
      case "aiAccess":
        return isPlatformAdmin ? <ManageAccessLLMModels /> : null;
      case "feedback":
        return isPlatformAdmin || isAdmin ? <ManageFeedback /> : null;
      case "useCases":
        return isPlatformAdmin || isAdmin ? <ManageUseCases /> : null;
      case "useCasePrompts":
        return isPlatformAdmin || isAdmin ? (
          <ManageUseCasePromptsListView />
        ) : null;
      case "workspaces":
        return isPlatformAdmin || isAdmin ? <ManageWorkspacesListView /> : null;
      case "templates":
        return isPlatformAdmin || isAdmin ? <ManageTemplates /> : null;
      case "helpPageDocuments":
        return isPlatformAdmin ? <ManageHelpPageDocuments /> : null;
      default:
        return null;
    }
  };

  return (
    <div className={mergeClasses("App", classes.container)}>
      <div className={classes.navPanel}>
        <NavigationPanel />
      </div>

      <AdminSidebar
        selectedKey={selectedSection}
        onSelect={handleSelect}
        userRole={userRole}
      />

      <div
        className={mergeClasses(
          classes.contentContainer,
          isMobile && classes.contentContainerMobile,
        )}
      >
        <div className={classes.contentBody}>{renderContent()}</div>
      </div>
    </div>
  );
};
